/*
 * HomeCrest Layer 4 - Visual preview
 * ===========================================================================
 * Provider-neutral architecture for generating a VISUAL REPRESENTATION of a
 * validated configuration. No provider is connected. No image is generated.
 *
 *   Layer 3 configuration  ->  preview request  ->  provider  ->  preview
 *
 * THE CONFIGURATION IS THE SOURCE OF TRUTH. The image is a picture of it and
 * nothing more. Three things enforce that rather than merely asserting it:
 *
 *   1. `representation_only` and `is_representation` are set here and cannot
 *      be turned off by a provider - the returned object is re-stamped.
 *   2. A preview is displayable ONLY when status is "ready" AND its
 *      `generated_from` still equals the live configuration hash. Change the
 *      configuration and the old picture goes stale automatically.
 *   3. This module has no Shopify write path whatsoever. It cannot upload
 *      media, create a product or a variant, or touch a price, because no
 *      code to do any of that exists in it.
 *
 * The provider receives the VALIDATED CONFIGURATION, never the customer's raw
 * sentence. Layer 2 already interpreted that sentence and Layer 3 already
 * validated the result; handing the words to an image model as well would let
 * it re-interpret an instruction that has already been settled.
 */

const PREVIEW_SCHEMA = 'homecrest.preview/1';

const STATES = Object.freeze({
  PENDING: 'pending',
  GENERATING: 'generating',
  READY: 'ready',
  STALE: 'stale',
  FAILED: 'failed',
});

/* Wording the UI must carry when a preview is eventually shown. Held here so
   the claim travels with the data rather than being re-invented per surface. */
const DISCLAIMER =
  'AI-generated visual preview. Final product may vary. Custom specifications are subject to HomeCrest approval.';

/* ==========================================================================
   Preservation
   --------------------------------------------------------------------------
   Everything the customer did NOT ask to change must survive. Rather than
   listing what to protect per product, the full set is protected by default
   and only the tokens a requested change actually governs are released.
   A width change frees proportions; it does not free the drawer count.
   ========================================================================== */

const PRESERVE_ALL = Object.freeze([
  'design_identity', 'silhouette', 'structure', 'proportions',
  'component_count', 'leg_count', 'drawer_count', 'door_count', 'cushion_count',
  'arm_design', 'joinery', 'hardware',
  'materials', 'finish', 'colour', 'upholstery', 'texture',
]);

/*
 * Preservation is selected by a modification's TYPE, not by an axis the
 * customer was permitted to name. `type` is a closed set because the model has
 * to be told exactly what it may alter; the customer's REQUEST is open, and an
 * unrecognised type resolves to `structural` rather than being refused.
 */
const TYPE_PROFILES = Object.freeze({
  dimension: ['proportions'],
  finish: ['finish', 'colour'],
  material: ['materials', 'texture', 'colour'],
  upholstery: ['upholstery', 'colour', 'texture'],
  hardware: ['hardware'],
  addition: ['component_count', 'door_count', 'drawer_count', 'proportions'],
  removal: ['component_count', 'door_count', 'drawer_count', 'proportions'],
  structural: ['component_count', 'silhouette', 'structure', 'proportions', 'door_count', 'drawer_count'],
  style: ['finish', 'colour', 'materials', 'texture', 'hardware'],
});

const FALLBACK_TYPE = 'structural';

/* A real variant option still releases only what that option governs. */
const SELECTION_TYPES = Object.freeze({
  finish: 'finish', colour: 'finish', size: 'dimension',
  fabric: 'upholstery', upholstery: 'upholstery', material: 'material',
});

/** Never released by any profile - what keeps the piece recognisably itself. */
const ALWAYS_PROTECTED = Object.freeze(['design_identity', 'joinery']);

function profileFor(type) {
  return TYPE_PROFILES[type] ?? TYPE_PROFILES[FALLBACK_TYPE];
}

/**
 * Derives the preserve list for a configuration. Public so it can be
 * inspected and tested independently of any provider.
 */
function derivePreservation(configuration) {
  const released = new Set();

  for (const s of configuration?.variant_selections ?? []) {
    for (const token of profileFor(SELECTION_TYPES[s.axis] ?? FALLBACK_TYPE)) released.add(token);
  }
  /* A request that named several kinds of change releases the union of their
     profiles. Picking one - even the broadest - would still tell the model to
     preserve an attribute the customer explicitly asked to change: "wider and
     change the finish" under `structural` alone would protect the finish. */
  for (const m of configuration?.modifications ?? []) {
    const families = m.families?.length ? m.families : [m.type];
    for (const family of families) {
      for (const token of profileFor(family)) released.add(token);
    }
  }
  for (const token of ALWAYS_PROTECTED) released.delete(token);

  return PRESERVE_ALL.filter((token) => !released.has(token));
}

/**
 * The explicit, ORDERED change list, so a provider never has to infer intent.
 *
 * `source_text` is deliberately absent. The customer's raw sentence stays with
 * the design record for a human to read; it is never handed to a model, which
 * would let it re-interpret a decision Layer 2 already settled.
 */
function deriveModifications(configuration) {
  return [
    ...(configuration?.variant_selections ?? []).map((s) => ({
      type: SELECTION_TYPES[s.axis] ?? FALLBACK_TYPE,
      axis: s.axis,
      value: s.value,
      unit: null,
      instruction: `${s.axis} changed to ${s.value}`,
    })),
    ...(configuration?.modifications ?? []).map((m) => ({
      type: m.type ?? FALLBACK_TYPE,
      families: [...(m.families ?? [])],
      axis: m.axis ?? null,
      value: m.value ?? null,
      unit: m.unit ?? null,
      instruction: m.instruction ?? '',
    })),
  ];
}

/* ==========================================================================
   Provider registry
   ========================================================================== */

const providers = new Map();
let activeProvider = 'mock';

function registerProvider(name, provider) {
  if (!provider || typeof provider.generate !== 'function') {
    throw new TypeError('provider must expose generate()');
  }
  providers.set(name, provider);
}

function setProvider(name) {
  if (!providers.has(name)) throw new Error(`unknown provider: ${name}`);
  activeProvider = name;
}

function listProviders() {
  return [...providers.keys()];
}

/* ==========================================================================
   Request
   ========================================================================== */

/**
 * Normalised request handed to whichever provider is active.
 *
 * `source_images` is an ARRAY from the outset - a single view is rarely
 * enough to modify a piece of furniture faithfully, and a provider that can
 * use front/three-quarter/side/rear should not be limited by this contract.
 *
 * `base_3d_model` exists and stays null. A future pipeline may render from
 * geometry instead of pixels; the request shape should not have to change
 * when it does.
 */
function buildRequest(configuration, sourceImages = [], options = {}) {
  return Object.freeze({
    schema: PREVIEW_SCHEMA,
    configuration_hash: configuration.configuration_hash,

    product: {
      id: configuration.product_id,
      handle: configuration.product_handle,
      title: options.productTitle ?? null,
      type: options.productType ?? null,
    },
    base_variant: {
      id: configuration.base_variant_id,
      title: options.variantTitle ?? null,
    },

    source_images: [...sourceImages],
    base_3d_model: options.base3dModel ?? null,

    variant_selections: configuration.variant_selections.map((s) => ({ ...s })),
    modifications: deriveModifications(configuration),
    modify: deriveModifications(configuration),
    preserve: derivePreservation(configuration),

    representation_only: true,
  });
}

/* ==========================================================================
   Generate
   ========================================================================== */

function previewRecord(fields) {
  return Object.freeze({
    schema: PREVIEW_SCHEMA,
    status: STATES.PENDING,
    generated_from: null,
    image_url: null,
    images: [],
    provider: null,
    error: null,
    is_representation: true,
    disclaimer: DISCLAIMER,
    ...fields,
    // Re-stamped last: a provider cannot clear these by returning them false.
    ...{ is_representation: true, disclaimer: DISCLAIMER },
  });
}

/**
 * Runs the active provider. Never throws; a failure is a `failed` preview.
 */
async function generate(configuration, sourceImages = [], options = {}) {
  if (!configuration?.configuration_hash) {
    return previewRecord({ status: STATES.FAILED, error: { code: 'no_configuration', message: 'A validated configuration is required.' } });
  }
  // A configuration that failed validation never reaches a provider.
  if (configuration.schema !== 'homecrest.configuration/2') {
    return previewRecord({ status: STATES.FAILED, error: { code: 'bad_configuration', message: 'Unrecognised configuration schema.' } });
  }
  if (!Array.isArray(sourceImages) || sourceImages.length === 0) {
    return previewRecord({ status: STATES.FAILED, error: { code: 'no_source_image', message: 'At least one source image is required.' } });
  }

  const provider = providers.get(activeProvider);
  if (!provider) {
    return previewRecord({ status: STATES.FAILED, error: { code: 'no_provider', message: `Unknown provider: ${activeProvider}` } });
  }

  const request = buildRequest(configuration, sourceImages, options);

  try {
    const result = await provider.generate(request);
    if (!result?.image_url) {
      return previewRecord({ status: STATES.FAILED, provider: activeProvider, generated_from: request.configuration_hash, error: { code: 'provider_returned_nothing', message: 'Provider returned no image.' } });
    }
    return previewRecord({
      status: STATES.READY,
      // Bound to the hash of the configuration ACTUALLY sent, not to whatever
      // a provider might echo back.
      generated_from: request.configuration_hash,
      image_url: result.image_url,
      images: Array.isArray(result.images) ? result.images : [result.image_url],
      provider: activeProvider,
      /* Whether the PROXY served this from its cache rather than generating
         it. Reported, never trusted for anything but accounting: Layer 5 uses
         it to avoid charging a free result against the session ceiling. A
         provider that omits it is treated as having generated. */
      cached: result.cached === true,
    });
  } catch (error) {
    return previewRecord({ status: STATES.FAILED, provider: activeProvider, generated_from: request.configuration_hash, error: { code: 'provider_failed', message: String(error?.message ?? error) } });
  }
}

/* ==========================================================================
   Staleness
   ========================================================================== */

/**
 * Re-checks a preview against the CURRENT configuration. A preview whose hash
 * no longer matches becomes stale - it is a picture of something the customer
 * has since changed.
 */
function reconcile(preview, configuration) {
  if (!preview) return null;
  if (preview.status === STATES.FAILED) return preview;

  const current = configuration?.configuration_hash ?? null;
  if (preview.generated_from && current && preview.generated_from !== current) {
    return previewRecord({ ...preview, status: STATES.STALE });
  }
  return preview;
}

/** The single gate any UI must pass before showing a preview. */
function isDisplayable(preview, configuration) {
  return Boolean(
    preview &&
      preview.status === STATES.READY &&
      preview.generated_from &&
      configuration?.configuration_hash &&
      preview.generated_from === configuration.configuration_hash
  );
}

/* ==========================================================================
   Source images - referenced, never copied
   ========================================================================== */

/**
 * Reads the ORIGINAL product gallery straight from the rendered page. The
 * originals are referenced by URL; nothing is uploaded, replaced or written
 * back to Shopify.
 */
function readSourceImages(root = document, limit = 4) {
  const scope = root.querySelector?.('.hc-pdp-editorial') ?? root;
  const imgs = [...scope.querySelectorAll('.product-media img, slideshow-slide img')];
  const urls = imgs
    .map((img) => img.currentSrc || img.src)
    .filter(Boolean)
    .map((u) => u.split('?')[0]);
  return [...new Set(urls)].slice(0, limit);
}

/* ==========================================================================
   Mock providers - for testing the pipeline, not for producing pictures
   ========================================================================== */

registerProvider('mock', {
  name: 'mock',
  async generate(request) {
    // Deliberately not an image. Nothing renders it; it only proves the
    // request reached a provider and came back bound to the right hash.
    return {
      image_url: `mock://preview/${request.configuration_hash.replace('sha256:', '').slice(0, 16)}`,
      images: request.source_images.map(
        (_, i) => `mock://preview/${request.configuration_hash.replace('sha256:', '').slice(0, 16)}/view-${i}`
      ),
      // Nothing was generated and nothing was billed, but a mock is not a
      // cache hit either; it reports the same shape the real provider does.
      cached: false,
    };
  },
});

registerProvider('mock-failing', {
  name: 'mock-failing',
  async generate() {
    throw new Error('mock provider failure');
  },
});

registerProvider('mock-empty', {
  name: 'mock-empty',
  async generate() {
    return {};
  },
});

/*
 * The REAL provider below holds no credential. It posts to a Cloudflare Worker
 * that owns the Gemini key as an encrypted secret, and receives back only an
 * image URL. Nothing in Liquid, theme settings, metafields or this file carries
 * a key, and the endpoint URL is not one - it is a public address.
 * ==========================================================================
 */

/**
 * A random per-tab id for ONE visualisation session.
 *
 * Not an identifier for a person: it is generated in the browser, never linked
 * to a customer, and dies with the tab. Its only job is to let the proxy count
 * paid generations per session, so a single sitting cannot run up an unbounded
 * bill. If sessionStorage is unavailable the value is per-page instead, which
 * degrades the cap without breaking generation.
 */
let memorySessionId = null;
function previewSessionId() {
  const mint = () =>
    `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
  try {
    const existing = sessionStorage.getItem('hc_preview_session');
    if (existing) return existing;
    const fresh = mint();
    sessionStorage.setItem('hc_preview_session', fresh);
    return fresh;
  } catch {
    memorySessionId ??= mint();
    return memorySessionId;
  }
}

/** A fresh id per generation attempt. Random, meaningless outside this call. */
function newGenerationId() {
  try {
    if (crypto?.randomUUID) return crypto.randomUUID();
  } catch {
    /* fall through to the manual form below */
  }
  return `g_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
}

registerProvider('gemini', {
  name: 'gemini',
  async generate(request) {
    const endpoint = window.HomeCrestPreviewConfig?.endpoint;
    if (!endpoint) throw new Error('No preview endpoint is configured.');

    /* The proxy is sent the STRUCTURED configuration and nothing else. The
       customer's sentence is deliberately absent: Layer 2 interpreted it and
       Layer 3 validated the result, so re-sending the words would let an image
       model reopen a decision that is already settled.

       `request.preserve` is also not sent. The Worker re-derives the protected
       list from the modifications itself, because a value that travelled
       through the browser cannot be trusted to describe its own limits. */
    const response = await fetch(`${String(endpoint).replace(/\/+$/, '')}/preview`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        configuration: {
          schema: 'homecrest.configuration/2',
          configuration_hash: request.configuration_hash,
          product_handle: request.product.handle,
          base_variant_id: request.base_variant.id,
          variant_selections: request.variant_selections,
          modifications: request.modify,
        },
        source_images: request.source_images,
        /* An opaque per-tab value identifying ONE visualisation session, so the
           proxy can cap how many PAID generations that session may start. It is
           random, lives only in sessionStorage, and identifies no one. */
        session_id: previewSessionId(),
        /* Identifies THIS click. The proxy records it and refuses a second
           request carrying the same id, so a replayed POST - a browser retry,
           a resent request, a duplicated fetch - cannot be paid for twice.
           Distinct from the configuration hash, which dedupes the same DESIGN
           rather than the same CLICK. */
        generation_id: newGenerationId(),
        preview_request: {
          schema: request.schema,
          representation_only: true,
        },
      }),
    });

    if (!response.ok) {
      let detail = String(response.status);
      try {
        detail = (await response.json()).error ?? detail;
      } catch {
        /* a non-JSON error body carries nothing useful; the status stands */
      }
      throw new Error(detail);
    }

    const body = await response.json();
    if (!body?.image_url) throw new Error('Proxy returned no image.');

    /* The hash is NOT taken from here. `generate()` stamps generated_from from
       the request it actually sent, so a proxy cannot bind an image to a
       configuration the customer is not looking at. */
    return { image_url: body.image_url, images: [body.image_url], cached: body.cached === true };
  },
});

window.HomeCrestPreview = {
  SCHEMA: PREVIEW_SCHEMA,
  STATES,
  DISCLAIMER,
  PRESERVE_ALL,
  registerProvider,
  setProvider,
  listProviders,
  derivePreservation,
  deriveModifications,
  buildRequest,
  generate,
  reconcile,
  isDisplayable,
  readSourceImages,
};
